import { useState, useEffect } from 'react';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Card } from './ui/card';
import { Loader2, Star, Sparkles, Crown, Trophy } from 'lucide-react';
import { cn } from '../lib/utils';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';



const TIER_ICONS = {
  supporter: Star,
  super_fan: Sparkles,
  vip_fan: Crown,
  legend: Trophy
};

export function ListenerTierBadge({ tier, size = 'default', showLabel = true }) {
  if (!tier) return null;
  
  const Icon = TIER_ICONS[tier.tier_id] || Star;
  const sizeClasses = {
    small: 'text-xs px-1.5 py-0.5',
    default: 'text-sm px-2 py-1',
    large: 'text-base px-3 py-1.5'
  };
  
  return (
    <Badge 
      className={cn(sizeClasses[size], "flex items-center gap-1")}
      style={{ 
        backgroundColor: `${tier.badge_color}20`, 
        borderColor: `${tier.badge_color}50`,
        color: tier.badge_color 
      }}
      variant="outline"
      data-testid={`tier-badge-${tier.tier_id}`}
    >
      <Icon className={cn(size === 'small' ? 'w-3 h-3' : 'w-4 h-4')} />
      {showLabel && tier.name}
    </Badge>
  );
}

export function ListenerTierProgress({ userId, getAuthHeaders, compact = false }) {
  const [tierInfo, setTierInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTierInfo();
  }, [userId]);

  const fetchTierInfo = async () => {
    try {
      const url = userId 
        ? `${API}/listener/tier/${userId}`
        : `${API}/listener/tier`;
      
      const headers = getAuthHeaders ? getAuthHeaders() : {};
      const response = await fetch(url, { headers });
      
      if (response.ok) {
        setTierInfo(await response.json());
      }
    } catch (error) {
      console.error('Error fetching tier info:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />;
  }

  if (!tierInfo) return null;

  if (compact) {
    return (
      <div className="flex items-center gap-2" data-testid="tier-progress-compact">
        {tierInfo.current_tier ? (
          <ListenerTierBadge tier={tierInfo.current_tier} size="small" />
        ) : (
          <span className="text-xs text-muted-foreground">No tier yet</span>
        )}
        {tierInfo.next_tier && (
          <span className="text-xs text-muted-foreground">
            {tierInfo.coins_to_next_tier.toLocaleString()} to {tierInfo.next_tier.name}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="tier-progress">
      {/* Current Tier */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {tierInfo.current_tier ? (
            <>
              <ListenerTierBadge tier={tierInfo.current_tier} />
              <span className="text-sm text-muted-foreground">
                {tierInfo.total_coins_spent.toLocaleString()} coins spent
              </span>
            </>
          ) : (
            <span className="text-sm text-muted-foreground">
              Spend coins to unlock tiers!
            </span>
          )}
        </div>
      </div>

      {/* Progress to Next Tier */}
      {tierInfo.next_tier && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">
              Progress to {tierInfo.next_tier.name}
            </span>
            <span style={{ color: tierInfo.next_tier.badge_color }}>
              {tierInfo.progress_percent}%
            </span>
          </div>
          <Progress 
            value={tierInfo.progress_percent} 
            className="h-2"
            style={{ 
              '--progress-color': tierInfo.next_tier.badge_color 
            }}
          />
          <p className="text-xs text-muted-foreground">
            {tierInfo.coins_to_next_tier.toLocaleString()} more coins needed
          </p>
        </div>
      )}

      {/* Max tier reached */}
      {tierInfo.current_tier && !tierInfo.next_tier && (
        <p className="text-xs text-center" style={{ color: tierInfo.current_tier.badge_color }}>
          🎉 You've reached the highest tier!
        </p>
      )}
    </div>
  );
}

export function ListenerTierCard({ getAuthHeaders }) {
  const [tierInfo, setTierInfo] = useState(null);
  const [allTiers, setAllTiers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tierRes, allTiersRes] = await Promise.all([
        fetch(`${API}/listener/tier`, { headers: getAuthHeaders() }),
        fetch(`${API}/listener/tiers`)
      ]);
      
      if (tierRes.ok) setTierInfo(await tierRes.json());
      if (allTiersRes.ok) {
        const data = await allTiersRes.json();
        setAllTiers(data.tiers || []);
      }
    } catch (error) {
      console.error('Error fetching tier data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="glass-card border-border p-6">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </Card>
    );
  }

  return (
    <Card className="glass-card border-border p-6" data-testid="listener-tier-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
          <Trophy className="w-5 h-5 text-purple-400" />
        </div>
        <div>
          <h3 className="font-heading font-bold">Listener Tier</h3>
          <p className="text-sm text-muted-foreground">Earn perks by supporting creators</p>
        </div>
      </div>

      {/* Current Progress */}
      <ListenerTierProgress getAuthHeaders={getAuthHeaders} />

      {/* All Tiers Overview */}
      <div className="mt-6 pt-4 border-t border-border">
        <p className="text-xs text-muted-foreground mb-3">All Tiers</p>
        <div className="grid grid-cols-2 gap-2">
          {allTiers.map(tier => {
            const isUnlocked = tierInfo?.total_coins_spent >= tier.min_coins;
            const isCurrent = tierInfo?.current_tier?.tier_id === tier.tier_id;
            const Icon = TIER_ICONS[tier.tier_id] || Star;
            
            return (
              <div 
                key={tier.tier_id}
                className={cn(
                  "p-2 rounded-lg border text-center transition-all",
                  isCurrent 
                    ? "border-2" 
                    : isUnlocked 
                      ? "border-border bg-muted/30" 
                      : "border-border opacity-50"
                )}
                style={isCurrent ? { borderColor: tier.badge_color } : {}}
              >
                <Icon 
                  className="w-5 h-5 mx-auto mb-1" 
                  style={{ color: isUnlocked ? tier.badge_color : undefined }}
                />
                <p className="text-xs font-medium" style={{ color: isUnlocked ? tier.badge_color : undefined }}>
                  {tier.name}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {tier.min_coins.toLocaleString()} coins
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}
